<script lang="ts">  import { m } from '$lib/paraglide/messages';

  import { translation } from '$lib/locale/locale';
  import type { Tag } from '$lib/models/tag';
  import { createEventDispatcher } from 'svelte';
  import crossIcon from '$lib/assets/search/cross.svg';
  import { page } from '$app/state';
  import { assetLink } from '$lib/utils/asset';

  const dispatch = createEventDispatcher<{
    categoryChange: Tag;
    removeBpm: void;
    removeLength: void;
    removeAll: void;
    removeQuery: void;
    removeSimilarTo: void;
  }>();

  const { langHref } = translation();

  export let selectedCategories: Tag[];
  export let minBpm: number;
  export let maxBpm: number;
  export let minLength: number;
  export let maxLength: number;
  export let selectedMinBpm: number;
  export let selectedMaxBpm: number;
  export let selectedMinLength: number;
  export let selectedMaxLength: number;
  export let selectedSimilarTo: number;
  export let query: string;

  $: queryText = query.length > 15 ? query.slice(0, 15) + '...' : query;

  function remove(category: Tag) {
    if (!category.selected) {
      return;
    }
    let idsToRemove = [category.id];
    category.selected = false;
    if (category.children?.length) {
      category.children.forEach((c) => {
        idsToRemove.push(c.id);
        c.selected = false;
      });
    }
    selectedCategories = selectedCategories.filter((c) => !idsToRemove.includes(c.id));
    dispatch('categoryChange', category);
  }
</script>

<div class="selected-filters">
  <ul class:show={true}>
    {#if selectedSimilarTo}
      <li class="selected">
        <p>
          <span class="text-secondary">{m['search.similar-to']()}</span>
          {#if page.data?.results?.similarTo?.title}
            <a href={assetLink(langHref, page.data?.results?.similarTo)}>
              {page.data?.results?.similarTo?.title}
            </a>
          {:else}
            ...
          {/if}
        </p>
        <button class="remove" on:click={() => dispatch('removeSimilarTo')}>
          <img src={crossIcon} alt="{m['actions.remove']()} {m['actions.search']()}" loading="lazy" />
        </button>
      </li>
    {/if}
    {#if query}
      <li class="selected query">
        <p>{m['actions.search']()}: {queryText}</p>
        <button class="remove" on:click={() => dispatch('removeQuery')}>
          <img src={crossIcon} alt="{m['actions.remove']()} {m['actions.search']()}" loading="lazy" />
        </button>
      </li>
    {/if}
    {#each selectedCategories as category (category.id)}
      <li class="selected">
        <p>{category.name}</p>
        <button class="remove" on:click={() => remove(category)}>
          <img src={crossIcon} alt={m['actions.remove']()} loading="lazy" />
        </button>
      </li>
    {/each}
    {#if selectedMinBpm != minBpm || selectedMaxBpm != maxBpm}
      <li class="selected">
        <p>{selectedMinBpm} {m['common.bpm']()} - {selectedMaxBpm} {m['common.bpm']()}</p>
        <button class="remove" on:click={() => dispatch('removeBpm')}>
          <img src={crossIcon} alt={m['actions.remove']()} loading="lazy" />
        </button>
      </li>
    {/if}
    {#if selectedMinLength != minLength || selectedMaxLength != maxLength}
      <li class="selected">
        <p>{selectedMinLength} {m['search.filter-minutes']()} - {selectedMaxLength} {m['search.filter-minutes']()}</p>
        <button class="remove" on:click={() => dispatch('removeLength')}>
          <img src={crossIcon} alt={m['actions.remove']()} loading="lazy" />
        </button>
      </li>
    {/if}
  </ul>
</div>

<style>
  .selected-filters {
    display: flex;
    align-items: center;
    font-size: 16px;
    color: var(--black-coral);
    font-weight: 500;
  }

  ul {
    display: none;
    flex-wrap: wrap;
    list-style: none;
    margin: 0;
    padding: 0;
    margin-top: 16px;
  }

  ul.show {
    display: flex;
  }

  ul li {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    background-color: white;
    border: 1px solid #f0f0f0;
    margin-right: 8px;
    margin-bottom: 8px;
    color: var(--text-secondary);
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
  }

  ul li img {
    width: 10px;
    height: 10px;
    object-fit: contain;
  }

  ul li button {
    padding: 0;
    width: 14px;
    height: 14px;
    margin-right: 9px;
    margin-left: -4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
  }

  ul li p {
    display: flex;
    color: var(--black-coral);
    padding: 4px 8px;
    font-size: 1.167rem;
    font-weight: 400;
    white-space: nowrap;
    margin: 0;
  }

  ul li:hover {
    border-color: var(--gainsboro);
    background-color: #f3f3f7;
    text-decoration: none;
  }

  ul li.selected {
    border-color: var(--charleston-grey);
  }

  ul li.selected p {
    color: var(--charleston-grey);
  }

  .text-secondary {
    color: var(--text-secondary);
    margin-right: 4px;
  }

  @media (max-width: 900px) {
    ul {
      margin: 0;
      flex-wrap: nowrap;
      overflow-x: auto;
      margin-left: -16px;
      margin-right: -16px;
      padding-left: 16px;
      padding-right: 16px;
    }
  }
</style>
